import * as Icons from 'lucide-react';
import { SiteLayout } from '../../components/site/SiteLayout';
import { PillLink } from '../../components/site/Pill';
import { BENEFITS, TESTIMONIALS, INTERIORS } from '../../lib/site';

const Icon = ({ name, className }: { name: string; className?: string }) => {
  const C = (Icons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[name] ?? Icons.Circle;
  return <C className={className} />;
};

export default function SiteLivingWithUs() {
  return (
    <SiteLayout>
      <section className="site-narrow pt-20 lg:pt-32">
        <p className="eyebrow">Living With Us</p>
        <h1 className="display mt-4 max-w-[900px] text-[44px] text-ink-900 sm:text-[60px] lg:text-[80px]">Say goodbye to <em>rogue landlords</em></h1>
        <p className="mt-8 max-w-2xl text-[18px] leading-[1.7] text-ink-600">We're a professionally managed property rental service with numerous advantages. We own every home we let, we look after it ourselves, and we answer the phone.</p>
      </section>

      <section className="site-narrow py-20 lg:py-28">
        <div className="grid gap-px overflow-hidden rounded-[10px] border border-ink-200 bg-ink-200 sm:grid-cols-2 lg:grid-cols-3">
          {BENEFITS.map((b) => (
            <div key={b.title} className="bg-white p-8 lg:p-10">
              <Icon name={b.icon} className="h-5 w-5 text-bronze-500" />
              <h3 className="display mt-5 text-[26px] text-ink-900">{b.title}</h3>
              <p className="mt-3 text-[15px] leading-[1.75] text-ink-600">{b.body}</p>
            </div>
          ))}
          <div className="flex flex-col justify-between bg-ink-900 p-8 text-white lg:p-10">
            <div>
              <Icons.Smartphone className="h-5 w-5 text-bronze-300" />
              <h3 className="display mt-5 text-[26px]">Everything from your phone</h3>
              <p className="mt-3 text-[15px] leading-[1.75] text-white/75">Report a repair with a photo, see your rent and documents, and ask our assistant anything about your home — in the Lotus app or on the web.</p>
            </div>
            <PillLink to="/login" variant="white" small className="mt-8 self-start">Resident sign in</PillLink>
          </div>
        </div>
      </section>

      <section className="site-container pb-20 lg:pb-28">
        <div className="grid gap-4 sm:grid-cols-3">
          <img src={INTERIORS.kitchen} alt="" className="aspect-[4/3] w-full rounded-[10px] object-cover sm:col-span-2 sm:aspect-[16/9]" loading="lazy" />
          <img src={INTERIORS.bathroom} alt="" className="hidden aspect-[3/4] w-full rounded-[10px] object-cover sm:block" loading="lazy" />
        </div>
      </section>

      <section className="bg-cream">
        <div className="site-narrow py-24 lg:py-36">
          <h2 className="display text-[40px] text-ink-900 sm:text-[52px] lg:text-[64px]">What Our <em>Residents</em> Say</h2>
          <div className="mt-14 grid gap-10 md:grid-cols-2 lg:gap-16">
            {TESTIMONIALS.map((t) => (
              <figure key={t.name} className="border-l-2 border-bronze-400 pl-6 lg:pl-8">
                <blockquote className="display text-[24px] leading-[1.35] text-ink-900 sm:text-[28px]">“{t.quote}”</blockquote>
                <figcaption className="mt-5 text-[13px] font-medium uppercase tracking-[0.18em] text-ink-500">{t.name} · Lotus Living resident</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="site-narrow py-24 text-center lg:py-36">
        <h2 className="display mx-auto max-w-[760px] text-[40px] text-ink-900 sm:text-[52px] lg:text-[64px]"><em>Experience the</em> Lotus Living difference</h2>
        <PillLink to="/rent" className="mt-10 lg:mt-14">Find your new home</PillLink>
      </section>
    </SiteLayout>
  );
}
